import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { ChevronDown } from "lucide-react";

// V1.25.1 · Fix z-index / stacking del dropdown:
// - El contenedor exterior tiene `overflow-x-auto` (necesario para móvil) que
//   clipa cualquier dropdown en `position:absolute`. Solución: usar
//   `position:fixed` calculando la posición contra `getBoundingClientRect()`
//   del botón + `React.createPortal` a `document.body` para escapar de
//   TODO stacking context.
// - z-index 60 (por encima del sticky header 30).

function isGroup(item) {
  return Array.isArray(item.children) && item.children.length > 0;
}

function activeInGroup(item, activeKey) {
  if (!isGroup(item)) return item.key === activeKey;
  return item.children.some((c) => c.key === activeKey);
}

function labelForActive(item, activeKey) {
  if (!isGroup(item)) return null;
  const found = item.children.find((c) => c.key === activeKey);
  return found ? found.label : null;
}

function GroupButton({ item, activeKey, onSelect }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const Icon = item.icon;
  const active = activeInGroup(item, activeKey);
  const activeLabel = labelForActive(item, activeKey);

  // Recompute panel position when opened + on resize / scroll.
  const recompute = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom, left: r.left });
  };
  useLayoutEffect(() => {
    if (!open) return;
    recompute();
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
  }, [open]);

  useEffect(() => {
    if (!active) setOpen(false);
  }, [active, activeKey]);

  useEffect(() => {
    function onDocClick(e) {
      const inBtn = btnRef.current && btnRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inBtn && !inPanel) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const panel = open ? ReactDOM.createPortal(
    <div
      ref={panelRef}
      style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 60 }}
      className="min-w-[220px] bg-white border border-neutral-200 rounded-b-lg shadow-xl overflow-hidden"
      data-testid={`nav-group-panel-${item.key}`}
    >
      {item.children.map((c) => {
        const ChildIcon = c.icon;
        const isActive = c.key === activeKey;
        return (
          <button
            key={c.key}
            type="button"
            data-testid={`nav-tab-${c.key}`}
            onClick={() => { onSelect(c.key); setOpen(false); }}
            className={
              "w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left transition-colors " +
              (isActive
                ? "bg-orange-50 text-orange-700 font-semibold"
                : "text-neutral-700 hover:bg-neutral-50")
            }
          >
            {ChildIcon && <ChildIcon className="h-4 w-4 shrink-0" />}
            <span>{c.label}</span>
          </button>
        );
      })}
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        data-testid={`nav-group-${item.key}`}
        onClick={() => setOpen((v) => !v)}
        className={
          "flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap " +
          (active
            ? "border-orange-500 text-orange-600"
            : "border-transparent text-neutral-500 hover:text-neutral-900")
        }
      >
        <Icon className="h-4 w-4" />
        <span>{item.label}</span>
        {activeLabel && (
          <span className="hidden lg:inline text-[10px] font-normal text-neutral-400 max-w-[110px] truncate">
            · {activeLabel}
          </span>
        )}
        <ChevronDown className={"h-3.5 w-3.5 transition-transform " + (open ? "rotate-180" : "")} />
      </button>
      {panel}
    </>
  );
}

function LeafButton({ item, activeKey, onSelect }) {
  const Icon = item.icon;
  const active = item.key === activeKey;
  return (
    <button
      type="button"
      data-testid={`nav-tab-${item.key}`}
      onClick={() => onSelect(item.key)}
      className={
        "flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap " +
        (active
          ? "border-orange-500 text-orange-600"
          : "border-transparent text-neutral-500 hover:text-neutral-900")
      }
    >
      <Icon className="h-4 w-4" />
      {item.label}
    </button>
  );
}

export default function TopNav({ items, activeKey, onSelect }) {
  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 overflow-x-auto" data-testid="top-nav">
      <div className="flex gap-1 sm:gap-2 -mb-px">
        {items.map((item) =>
          isGroup(item)
            ? <GroupButton key={item.key} item={item} activeKey={activeKey} onSelect={onSelect} />
            : <LeafButton  key={item.key} item={item} activeKey={activeKey} onSelect={onSelect} />
        )}
      </div>
    </div>
  );
}
